/**
 * Opening defaults for report and dashboard filters.
 *
 * A page whose only required filter is a period should open showing figures,
 * not an empty panel waiting for two dates to be typed. The financial year is
 * the period the business actually thinks in, so that is what it opens on.
 */

import { unansweredFilters, type ReportDefaultPeriod, type RuledFilter } from '@project/contracts'

export interface FilterLike {
  readonly key: string
  readonly control: string
  readonly required?: boolean
  readonly requiredGroup?: string
  readonly disables?: readonly string[]
  /** The window a date range opens on (reports, RPT-024). Default: the financial year. */
  readonly defaultPeriod?: ReportDefaultPeriod
  readonly split?: RuledFilter['split']
  readonly visibleWhen?: RuledFilter['visibleWhen']
  readonly requiredWhen?: RuledFilter['requiredWhen']
  /** A date range under keys of its own (RPT-016). */
  readonly range?: { readonly from: string; readonly to: string }
  /** What a select or date filter opens on: an option's value, or `today` for a date. */
  readonly defaultValue?: string
}

const iso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

/** 1 April to 31 March: the Indian financial year. */
export const financialYearStart = (today: Date = new Date()): string =>
  `${today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1}-04-01`

/**
 * A date range is carried as from/to, whatever the filter itself is called —
 * unless it names keys of its own. A select with a default opens on it.
 */
export const defaultFilterValues = (
  filters: readonly FilterLike[],
  today: Date = new Date(),
): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const filter of filters) {
    if (filter.control !== 'dateRange') {
      if (filter.defaultValue !== undefined && filter.defaultValue !== '') {
        out[filter.key] = filter.control === 'date' && filter.defaultValue === 'today' ? iso(today) : filter.defaultValue
      }
      continue
    }
    const from = filter.range?.from ?? 'from'
    const to = filter.range?.to ?? 'to'
    switch (filter.defaultPeriod ?? 'fy') {
      case 'none':
        break
      // A month, three months and a year back to today: the windows the
      // reference opens its valuation, approval and movement reports on.
      case 'lastMonth':
        out[from] = iso(new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()))
        out[to] = iso(today)
        break
      case 'last3Months':
        out[from] = iso(new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()))
        out[to] = iso(today)
        break
      case 'last12Months':
        out[from] = iso(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()))
        out[to] = iso(today)
        break
      default:
        out[from] = financialYearStart(today)
        out[to] = iso(today)
    }
  }
  return out
}

/**
 * The periods a business actually asks for (docs/15 §7.5).
 *
 * The financial year is the unit here — April to March — so "this year" means
 * the current one, not the last 365 days. The one-year cap the server enforces
 * is why there is no "everything" preset.
 */
export interface DatePreset {
  readonly key: string
  readonly label: string
  readonly range: (today?: Date) => { from: string; to: string }
}

const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const fyStartYear = (today: Date): number => (today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1)

export const DATE_PRESETS: readonly DatePreset[] = [
  {
    key: 'thisMonth',
    label: 'This month',
    range: (today = new Date()) => ({
      from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: isoDate(today),
    }),
  },
  {
    key: 'lastMonth',
    label: 'Last month',
    range: (today = new Date()) => ({
      from: isoDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: isoDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    }),
  },
  {
    key: 'thisQuarter',
    label: 'This quarter',
    range: (today = new Date()) => ({
      from: isoDate(new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)),
      to: isoDate(today),
    }),
  },
  {
    key: 'lastQuarter',
    label: 'Last quarter',
    range: (today = new Date()) => {
      // The quarter that ended, not the three months before today: a report
      // asked for "last quarter" is being reconciled against a closed period.
      const start = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 - 3, 1)
      const end = new Date(start.getFullYear(), start.getMonth() + 3, 0)
      return { from: isoDate(start), to: isoDate(end) }
    },
  },
  {
    key: 'thisFy',
    label: 'This FY',
    range: (today = new Date()) => ({ from: `${fyStartYear(today)}-04-01`, to: isoDate(today) }),
  },
  {
    key: 'lastFy',
    label: 'Last FY',
    range: (today = new Date()) => ({
      from: `${fyStartYear(today) - 1}-04-01`,
      to: `${fyStartYear(today)}-03-31`,
    }),
  },
]

/**
 * Whether these values answer everything the definition insists on —
 * including either/or groups and filters switched off by another (RPT-076).
 */
export const answersRequired = (
  filters: readonly FilterLike[],
  values: Record<string, string>,
): boolean => unansweredFilters(filters, values).length === 0
